/**
 * 应用详情面板 — 100%复刻原型 enterprise-brain-apps-clean.html
 * 侧边面板模式：从右侧滑入覆盖主内容区，左侧导航栏保持可见
 */
import { useEffect, useCallback, useState, useRef } from 'react';
import { sanitizeSVG } from '@/utils/sanitize';
import { resolveAppIcon } from '@/utils/appIcons';
import { isSourcePackage, isAIProject, getAppDownloadUrl, getAppViewUrl, getAppHtmlUrl, createWorkbuddyHandoff } from '@/services/apps';
import { useToast } from '@/components/Toast/useToast';
import { useAuthStore } from '@/stores/authStore';
import type { AppItem } from '@/services/apps';
import './AppModal.css';

interface AppDetailPanelProps {
  app: AppItem;
  catName?: string;
  onClose: () => void;
  onOpen?: () => void;
  onEdit?: (app: AppItem) => void;
  onUpdate?: (app: AppItem) => void;
  onDelete?: (app: AppItem) => void;
}

/* 图标组件 */
function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const paths: Record<string, string> = {
    back: 'M13 4L5 10l8 6',
    apps: 'm10 2 8 4v9l-8 4-8-4V6l8-4Zm0 8v9M2 6l8 4 8-4',
    code: 'M7 8l-4 4 4 4M13 8l4 4-4 4',
    user: 'M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-6 8a6 6 0 1 1 12 0H4Z',
    users: 'M14 15a4 4 0 1 0-8 0m4-5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm5 5a3 3 0 0 0-3-3m1-4a2.5 2.5 0 0 0 0-5',
    file: 'M12 2H5v16h10V5l-3-3Zm0 0v4h3M8 10h4m-4 3h4',
    download: 'M10 2v12M5 9l5 5 5-5M2 13v5h16v-5',
    eye: 'M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6Zm8 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
    edit: 'm13 2 5 5-11 11H2v-5L13 2Zm-3 3 5 5',
    upload: 'M10 14V2M5 7l5-5 5 5M2 13v5h16v-5',
    clock: 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM10 5v5.2l3 1.8',
    close: 'M5 5l10 10M15 5L5 15',
    external: 'M14 3h3v3m0-3L9 11M6 3H3v14h14v-3',
    trash: 'M3 5h14M7 5V2h6v3M5 5l1 13h8l1-13M8 8v6m4-6v6',
  };
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={paths[name] || paths.file} />
    </svg>
  );
}

/* HTML 详情预览弹窗组件 */
/* 应用预览区区 - 双列局部HTML片段，与原型一致 */

const IFRAME_BASE_WIDTH = 1050; // iframe原始宽度（桌面视口宽）

/* 单个预览面板：clip + scaled iframe */
function PreviewPanel({ html, scrollOffset, title }: {
  html: string;
  scrollOffset: number;
  title: string;
}) {
  const clipRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.333);

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setScale(w / IFRAME_BASE_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const iframeHeight = Math.ceil(220 / scale);

  return (
    <div ref={clipRef} className="app-preview-clip">
      <iframe
        title={title}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        srcDoc={html}
        style={{
          position: 'absolute',
          top: -scrollOffset,
          left: 0,
          width: IFRAME_BASE_WIDTH,
          height: iframeHeight + scrollOffset,
          border: 'none',
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
          pointerEvents: 'none',
          background: '#fff',
          display: 'block',
        }}
      />
    </div>
  );
}

/* 双列局部HTML片段预览 */
function AppPreviewIframe({ appId, appName }: { appId: string; appName: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const token = localStorage.getItem('eb-token');
        const url = getAppHtmlUrl(appId);
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('加载失败');
        const text = await res.text();
        if (!cancelled) setHtml(text);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [appId]);

  if (loading) {
    return (
      <div className="preview-loading">
        <div className="loading-spinner" />
        <span>加载预览中…</span>
      </div>
    );
  }
  if (error || !html) {
    return (
      <div className="preview-empty-state">
        <Icon name="file" size={32} />
        <span>无法加载预览</span>
        <small>点击「查看 HTML 详情」查看应用内容</small>
      </div>
    );
  }

  // 与原型 safePreview 一致的 CSP
  const csp = "default-src 'none'; style-src 'unsafe-inline'; img-src data: blob: https: http:; font-src data:; form-action 'none'; base-uri 'none'";
  const safeHtml = `<!doctype html><meta http-equiv="Content-Security-Policy" content="${csp}"><meta charset="utf-8">${html}`;

  return (
    <div className="app-preview-grid">
      <section className="app-preview-panel">
        <PreviewPanel html={safeHtml} scrollOffset={0} title={`${appName} 顶部`} />
      </section>
      <section className="app-preview-panel">
        <PreviewPanel html={safeHtml} scrollOffset={320} title={`${appName} 中部`} />
      </section>
    </div>
  );
}

function HtmlPreviewModal({ 
  app, 
  onClose 
}: { 
  app: AppItem; 
  onClose: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const token = localStorage.getItem('eb-token');
        const url = getAppHtmlUrl(String(app.id));
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error('无法加载 HTML 内容');
        }
        const text = await response.text();
        setHtml(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        setLoading(false);
      }
    };
    fetchHtml();
  }, [app.id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-mask open" onClick={onClose} style={{ zIndex: 100 }}>
      <div className="html-preview-modal" onClick={e => e.stopPropagation()}>
        <header className="html-preview-header">
          <div className="html-preview-title">
            <Icon name="file" size={18} />
            <span>{app.name} · HTML 详情</span>
          </div>
          <button className="btn btn-icon" aria-label="关闭" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="html-preview-content">
          {loading && (
            <div className="html-preview-loading">
              <div className="loading-spinner" />
              <span>加载 HTML 内容…</span>
            </div>
          )}
          {error && (
            <div className="html-preview-error">
              <strong>{error}</strong>
              <p>请确认应用文件可用，或稍后重试</p>
            </div>
          )}
          {!loading && !error && html && (
            <iframe
              className="html-preview-iframe"
              title={`${app.name} HTML 预览`}
              sandbox="allow-scripts"
              srcDoc={html}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function AppModal({ app, catName, onClose, onOpen, onEdit, onUpdate, onDelete }: AppDetailPanelProps) {
  const toast = useToast();
  const { user } = useAuthStore();
  const isGuest = user?.role === 'guest';
  const isSource = isSourcePackage(app);
  const isAI = isAIProject(app);
  const [downloading, setDownloading] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [showWorkbuddyConfirm, setShowWorkbuddyConfirm] = useState(false);
  const [workbuddyLoading, setWorkbuddyLoading] = useState(false);

  // 能显示预览：有文件内容的应用都尝试（后端会自动处理）
  // - html 文件：直接显示
  // - zip 包：后端自动查找内部 HTML，找不到返回占位页（也有内容）
  // - 无文件（file_key 为空）：不显示
  const canAutoCapture = !!(app.file_key) && (app.file_type === 'html' || app.file_type === 'zip');
  const appViewUrl = getAppViewUrl(String(app.id));

  // 下载处理
  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    toast.info('正在下载...');
    
    try {
      const token = localStorage.getItem('eb-token');
      const url = getAppDownloadUrl(String(app.id));
      
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `下载失败 (${response.status})`);
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = app.name;
      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const basicMatch = contentDisposition.match(/filename=["']?([^"';\n]+)/i);
          if (basicMatch) filename = basicMatch[1];
        }
      }
      if (!filename.includes('.')) {
        filename += app.file_type === 'zip' ? '.zip' : '.html';
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('已开始下载，请在浏览器中查看');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '下载失败');
    } finally {
      setDownloading(false);
    }
  }, [app, toast, downloading]);

  // 打开 WorkBuddy 确认弹窗
  const handleOpenWorkbuddy = useCallback(() => {
    setShowWorkbuddyConfirm(true);
  }, []);

  // 确认打开 WorkBuddy
  const handleConfirmWorkbuddy = useCallback(async () => {
    if (workbuddyLoading) return;
    setWorkbuddyLoading(true);
    
    try {
      // 生成唯一的幂等键
      const idempotencyKey = `wb-${app.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      
      // 调用后端创建 handoff
      const handoff = await createWorkbuddyHandoff(String(app.id), idempotencyKey);
      
      // 构建 WorkBuddy 协议链接 - 使用正确的 task?action=start&prompt= 格式
      const workbuddyUrl = `workbuddy://task?action=start&prompt=${encodeURIComponent(handoff.prompt || '')}`;
      
      // 跳转到 WorkBuddy
      window.location.href = workbuddyUrl;
      
      toast.success('已尝试打开 WorkBuddy，请在 WorkBuddy 中确认运行');
      setShowWorkbuddyConfirm(false);
    } catch (err) {
      console.error('WorkBuddy handoff failed:', err);
      const errorMsg = err instanceof Error ? err.message : '';
      
      // 根据错误类型显示不同提示
      if (errorMsg.includes('游客') || errorMsg.includes('登录')) {
        toast.error('游客账号不支持 WorkBuddy 功能，请登录后使用');
      } else if (errorMsg.includes('权限')) {
        toast.error('你没有该应用的 WorkBuddy 使用权限');
      } else {
        // WorkBuddy API 失败时，回退到直接打开查看
        toast.info('WorkBuddy 暂不可用，正在打开应用预览...');
        // 使用 fetch 获取内容后打开
        try {
          const token = localStorage.getItem('eb-token');
          const url = getAppViewUrl(String(app.id));
          const response = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (response.ok) {
            const html = await response.text();
            const blob = new Blob([html], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
          }
        } catch {
          // 忽略回退打开的错误
        }
      }
      setShowWorkbuddyConfirm(false);
    } finally {
      setWorkbuddyLoading(false);
    }
  }, [app, toast, workbuddyLoading]);

  const handleOpen = useCallback(async () => {
    if (onOpen) {
      onOpen();
    } else {
      // 使用 fetch + Authorization header 获取内容
      try {
        const token = localStorage.getItem('eb-token');
        const url = getAppViewUrl(String(app.id));
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || '无法加载应用');
        }
        const html = await response.text();
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '无法打开应用');
      }
    }
  }, [app.id, onOpen, toast]);

  const handleViewHtml = useCallback(async () => {
    // 使用 fetch + Authorization header 获取内容，然后在新窗口打开
    try {
      const token = localStorage.getItem('eb-token');
      const url = getAppViewUrl(String(app.id));
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '无法加载应用');
      }
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '无法打开 HTML 详情');
    }
  }, [app.id, toast]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(app);
    }
  }, [app, onEdit]);

  const handleUpdate = useCallback(() => {
    if (onUpdate) {
      onUpdate(app);
    }
  }, [app, onUpdate]);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(app);
    }
  }, [app, onDelete]);

  // 图标
  const thumbUrl = !app.icon_choice && app.thumbnail
    ? `/api/apps/thumbs/${app.thumbnail.replace(/^apps\/thumbs\//, '')}`
    : null;
  // 根据分类和 icon_choice 计算图标（使用共享系统）
  const { gradient: iconGrad, color: iconColor, svgPath } = resolveAppIcon(app.cat, app.icon_choice);
  const fallbackIcon = `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"><path d="${svgPath}"/></svg>`;

  // ESC 关闭
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !showHtmlPreview) onClose();
  }, [onClose, showHtmlPreview]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // 添加 body class
    document.body.classList.add('has-detail-panel');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('has-detail-panel');
    };
  }, [handleKeyDown]);

  // 格式化文件大小
  const formatSize = (bytes: number | undefined) => {
    if (!bytes) return '未知';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  // 版本记录
  const versions = app.hist?.length ? app.hist : [{ v: app.ver || app.version || '1.0.0', d: app.time, n: app.note || '首次发布' }];

  return (
    <>
      {/* 侧边详情面板 */}
      <div className="app-detail-panel">
        <section className="app-detail" aria-labelledby="detailTitle">
          {/* 顶部工具栏 */}
          <header className="detail-chrome">
            <button className="btn btn-icon detail-back" onClick={onClose} aria-label="返回" title="返回企业应用">
              <Icon name="back" size={20} />
            </button>
            <nav className="peek-breadcrumbs" aria-label="详情位置">
              <button onClick={onClose}>企业应用</button>
              <span aria-hidden="true">/</span>
              <button aria-current="page">{app.name}</button>
            </nav>
          </header>

          {/* 可滚动主体 */}
          <div className="detail-scroll" tabIndex={-1}>
            <article className="detail-document">
              {/* Hero 区域 */}
              <header className="detail-hero">
                <div className="detail-icon">
                  {thumbUrl ? (
                    <span className="app-icon app-icon-large app-icon-thumb">
                      <img src={thumbUrl} alt={app.name} onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        const parent = (e.target as HTMLImageElement).parentElement;
                        if (parent) {
                          parent.classList.remove('app-icon-thumb');
                          parent.style.background = iconGrad;
                          parent.style.color = iconColor;
                          parent.innerHTML = sanitizeSVG(fallbackIcon);
                        }
                      }}/>
                    </span>
                  ) : (
                    <span 
                      className="app-icon app-icon-large" 
                      style={{ background: iconGrad, color: iconColor }} 
                      dangerouslySetInnerHTML={{ __html: sanitizeSVG(fallbackIcon) }} 
                    />
                  )}
                </div>
                <div className="detail-heading">
                  <h1 className="peek-title" id="detailTitle">{app.name}</h1>
                  <p className="peek-description">{app.desc || '暂无应用介绍'}</p>
                  {/* 操作按钮 - 与原型完全一致 */}
                  <div className="detail-actions">
                    {/* 主按钮：打开 WorkBuddy */}
                    <button className="btn btn-primary btn-get" onClick={handleOpenWorkbuddy}>
                      打开 WorkBuddy
                    </button>
                    {/* 下载 ZIP 包 */}
                    <button className="btn btn-secondary" onClick={handleDownload} disabled={downloading}>
                      <Icon name="download" size={15} />
                      {downloading ? '下载中...' : '下载 ZIP 包'}
                    </button>
                    {/* 查看 HTML 详情 */}
                    <button className="btn btn-secondary" onClick={handleViewHtml}>
                      <Icon name="file" size={15} />
                      查看 HTML 详情
                    </button>
                    {/* 编辑信息 - Guest 用户隐藏 */}
                    {!isGuest && (
                      <button className="btn btn-secondary" onClick={handleEdit}>
                        <Icon name="edit" size={15} />
                        编辑信息
                      </button>
                    )}
                    {/* 更新版本 - Guest 用户隐藏 */}
                    {!isGuest && (
                      <button className="btn btn-secondary" onClick={handleUpdate}>
                        <Icon name="upload" size={15} />
                        更新版本
                      </button>
                    )}
                    {/* 删除应用 - Guest 用户隐藏 */}
                    {!isGuest && onDelete && (
                      <button className="btn btn-secondary danger" onClick={handleDelete}>
                        <Icon name="trash" size={15} />
                        删除
                      </button>
                    )}
                  </div>
                </div>
              </header>

              {/* 属性列表 - 按参考版样式，不加图标 */}
              <div className="detail-properties">
                <dl className="property-list">
                  <div className="property-row">
                    <dt>应用分类</dt>
                    <dd>{catName || '未分类'}</dd>
                  </div>
                  <div className="property-row">
                    <dt>当前版本</dt>
                    <dd>v{app.ver || app.version || '1.0.0'}</dd>
                  </div>
                  <div className="property-row">
                    <dt>发布人</dt>
                    <dd>{app.pub || '未知'}</dd>
                  </div>
                  <div className="property-row">
                    <dt>可见范围</dt>
                    <dd>{app.visibility === 'dept' ? '本部门' : '全公司'}</dd>
                  </div>
                  {app.file_size && (
                    <div className="property-row">
                      <dt>文件大小</dt>
                      <dd>{formatSize(app.file_size)}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* 预览区域 */}
              <section className="detail-preview">
                <h2>预览</h2>
                {canAutoCapture ? (
                  <AppPreviewIframe appId={String(app.id)} appName={app.name} />
                ) : (
                  <div className="empty-state">
                    <Icon name="file" size={25} />
                    <strong>应用文件预览</strong>
                    <p>
                      {app.app_kind === 'ai_project'
                        ? '这是一个 AI 流程包，包含提示词和配置文件。'
                        : app.app_kind === 'source_bundle'
                        ? '这是一个源码包，不包含可预览的网页入口。'
                        : '此应用不包含可预览的网页入口。'}
                      {' '}请下载后在本地使用。
                    </p>
                    <dl className="preview-pkg-info">
                      <div>
                        <dt>版本</dt>
                        <dd>v{app.ver || app.version || '1.0.0'}</dd>
                      </div>
                      {app.file_size ? (
                        <div>
                          <dt>大小</dt>
                          <dd>{formatSize(app.file_size)}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>发布者</dt>
                        <dd>{app.pub || '未知'}</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </section>

              {/* 内容区 */}
              <div className="detail-body">
                {/* 应用说明 */}
                <section className="doc-section">
                  <h2>应用说明</h2>
                  <p>{app.desc || '暂无应用介绍。'}</p>
                </section>

                {/* 功能亮点 */}
                {app.feats && app.feats.length > 0 && (
                  <section className="doc-section">
                    <h2>功能亮点</h2>
                    <p>{app.feats.join('、')}</p>
                  </section>
                )}

                {/* 动态数据配置 */}
                {app.api_url && (
                  <section className="doc-section">
                    <h2>动态数据配置</h2>
                    <dl className="property-list inline-props">
                      <div className="property-row">
                        <dt>数据接口 URL</dt>
                        <dd>{app.api_url}</dd>
                      </div>
                      <div className="property-row">
                        <dt>刷新间隔</dt>
                        <dd>{Number(app.data_refresh) > 0 ? `${app.data_refresh} 秒` : '不自动刷新'}</dd>
                      </div>
                    </dl>
                  </section>
                )}

                {/* 更新记录 - 差异11: 圆点改灰色，删除"当前版本"标签 */}
                <section className="doc-section">
                  <h2>更新记录</h2>
                  <div className="activity-timeline">
                    {versions.map((ver, i) => (
                      <article key={i}>
                        <span className="timeline-dot" />
                        <div>
                          <strong>v{ver.v}</strong>
                          <p>{ver.n || '版本更新'}</p>
                          <small>{ver.d}</small>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>

                {/* 使用范围 */}
                <section className="doc-section">
                  <h2>使用范围</h2>
                  <p>
                    {app.visibility === 'dept' 
                      ? `${app.dept || '本部门'}成员可查看和使用`
                      : '当前工作空间全体成员可查看和使用'}
                  </p>
                </section>
              </div>
            </article>
          </div>
        </section>
      </div>

      {/* HTML 详情预览弹窗 */}
      {showHtmlPreview && (
        <HtmlPreviewModal 
          app={app} 
          onClose={() => setShowHtmlPreview(false)} 
        />
      )}

      {/* WorkBuddy 确认弹窗 */}
      {showWorkbuddyConfirm && (
        <div className="modal-mask open" onClick={() => setShowWorkbuddyConfirm(false)} style={{ zIndex: 100 }}>
          <div className="dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="dialog-head">
              <h2>打开 WorkBuddy？</h2>
              <button className="btn btn-icon" onClick={() => setShowWorkbuddyConfirm(false)} aria-label="关闭">
                <Icon name="close" size={18} />
              </button>
            </header>
            <div className="dialog-body">
              <div className="wb-open-confirm">
                <p>将在 WorkBuddy 中打开「{app.name}」，并自动带入应用链接。</p>
                <p>进入 WorkBuddy 后，按回车即可开始。</p>
              </div>
            </div>
            <footer className="dialog-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowWorkbuddyConfirm(false)}
                disabled={workbuddyLoading}
              >
                取消
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmWorkbuddy}
                disabled={workbuddyLoading}
              >
                {workbuddyLoading ? '正在准备...' : '打开 WorkBuddy'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

export default AppModal;
